import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { GoodsReturnsService } from '../goods-returns.service'
import { GoodsReturnsRepository } from '../goods-returns.repository'
import {
  GoodsReturnField,
  GoodsReturnSearchParams,
} from '../models/goods-return-search.model'
import { GoodsReturn } from '../models/goods-return.entity'
import {
  GoodsReturnsListingData,
  GoodsReturnsListingPage,
} from '../models/goods-return-listing.model'
import { parseViewStateFiltersOptions, View } from '../../views'
import { Observable, of } from 'rxjs'
import { ChannelsService } from '../../channels'
import { PaymentsService } from '../../payments'
import { ReasonsService } from '../../reasons'
import { CountriesService } from '../../countries'
import { Tag } from '../../tags'
import { mergeById } from '../../../libs/filter.lib'

interface GoodsReturnsListingState {
  filters?: GoodsReturnSearchParams
  customFilters?: GoodsReturnSearchParams
  data?: GoodsReturn[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  extData?: GoodsReturnsListingData
  dropdownOptions?: GoodsReturnsListingData
  searchString?: string
  searchKeys?: GoodsReturnSearchParams
  fields?: GoodsReturnField[]
  view?: View
}

const GOODS_RETURNS_LISTING_INITIAL_STATE: GoodsReturnsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class GoodsReturnsListingStore {
  constructor(
    private goodsReturnsService: GoodsReturnsService,
    private goodsReturnsRepository: GoodsReturnsRepository,
    private channelsService: ChannelsService,
    private paymentsService: PaymentsService,
    private reasonsService: ReasonsService,
    private countriesService: CountriesService,
  ) {}

  private state = signal<GoodsReturnsListingState>(
    GOODS_RETURNS_LISTING_INITIAL_STATE,
  )

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly customFilters = computed(() => this.state().customFilters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly goodsReturns = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly fields = computed(() => this.state().fields)
  readonly view = computed(() => this.state().view)

  // Effects

  async search(
    params?: GoodsReturnSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.goodsReturnsRepository
        .searchGoodsReturns$(
          this.parseStateFilterParams(this.state(), params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async delete(goodsReturn: GoodsReturn): Promise<boolean> {
    if (goodsReturn.status !== 'RETURNING') {
      this.goodsReturnsRepository.notify({ code: 'WRONG_STATUS' })
      return false
    }

    if (goodsReturn.attachments?.length) {
      this.goodsReturnsRepository.notify({ code: 'ATTACHMENTS_FOUND' })
      return false
    }

    return new Promise<boolean>((resolve) => {
      this.goodsReturnsRepository
        .alert('GOODS_RETURN_DELETION', goodsReturn)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.goodsReturnsService.delete$(goodsReturn._id).subscribe({
              next: () => {
                resolve(true)
                this.goodsReturnsRepository.notify({
                  code: 'GOODS_RETURN_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof GoodsReturnsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<GoodsReturnsListingData[LookupKey]>
    >

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      channels: () => this.channelsService.list$(query),
      payments: () => this.paymentsService.list$(query),
      reasons: () => this.reasonsService.list$(query),
      countries: () => this.countriesService.list$(query),
      tags: () =>
        /* this.tagsService.list$({ ...query, target: 'goodsReturns' }) */ of([
          { value: '#partial', target: 'goodsReturns' },
        ]) as Observable<Tag[]>,
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: GoodsReturnsListingPage) {
    const extData = this.extData()
    const newExtData = {
      channels: mergeById(extData?.channels, data.extData?.channels),
      payments: mergeById(extData?.payments, data.extData?.payments),
      reasons: mergeById(extData?.reasons, data.extData?.reasons),
      countries: extData?.countries,
      tags: mergeById(extData?.tags, data.extData?.tags),
      orders: mergeById(extData?.orders, data.extData?.orders),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: GoodsReturnSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setView(view: View) {
    this.state.set({
      ...this.state(),
      view,
      fields: view.fields as GoodsReturnField[],
      customFilters: !view.filters
        ? {}
        : parseViewStateFiltersOptions<GoodsReturnSearchParams>(
            view.filters,
            {},
          ),
    })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: GoodsReturnSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  removeFilter(filter: GoodsReturnSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof GoodsReturnSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: GoodsReturnField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setSearchKeys(searchKeys?: GoodsReturnSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: GoodsReturnsListingState,
    filters?: GoodsReturnSearchParams,
  ): GoodsReturnSearchParams {
    let params: GoodsReturnSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }
}
